import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToSection, EASE } from "@/lib/scroll";

const HERO_IMG = "/photos/cottage-exterior.webp";

const LINES = [
  { text: "A Century of", cls: "" },
  { text: "Memories.", cls: "italic text-[#E8D8CF]" },
  { text: "Modernized for Summer.", cls: "" },
];

const BADGES = [
  "Sleeps 6 · 2 Bed + Nook",
  "Car-Free Sanctuary",
  "300 Steps to Connaught Beach",
];

export default function Hero() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const fade = useTransform(scrollY, [0, 550], [1, 0.25]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative flex min-h-screen items-end overflow-hidden bg-[#1E3323]"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          data-testid="hero-background-image"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
          src={HERO_IMG}
          alt="Heritage cottage nestled among tall pines"
          className="h-[115%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/50" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/15 to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-44 [text-shadow:0_1px_20px_rgba(0,0,0,0.55)] sm:px-10"
      >
        <div className="max-w-3xl rounded-[28px] border border-white/10 bg-black/40 p-7 backdrop-blur-md sm:p-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.9, ease: EASE }}
            className="mb-8 flex items-center gap-4"
          >
            <span className="h-px w-12 bg-[#C8634B]" />
            <p
              data-testid="hero-eyebrow"
              className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#F7F4EF]/90 sm:text-xs"
            >
              Victoria Beach · Manitoba · Est. 1922
            </p>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-serif text-4xl font-semibold leading-[1.06] tracking-tight text-[#F7F4EF] sm:text-6xl lg:text-7xl"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.35 + i * 0.15, ease: EASE }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9, ease: EASE }}
            className="mt-8 max-w-xl text-sm leading-relaxed text-[#F7F4EF] sm:text-lg"
          >
            Portions of this cottage are over 100 years old, thoughtfully
            modernized with a full IKEA kitchen, an outdoor shower, and a private
            back deck, a short bike ride from everything Victoria Beach has to
            offer.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-reserve-button"
              onClick={() => scrollToSection("#enquire")}
              className="rounded-full border border-[#C8634B] bg-[#C8634B] px-8 py-4 text-sm font-semibold tracking-wide text-[#F7F4EF] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#b4532e] hover:shadow-xl hover:shadow-black/25"
            >
              Enquire for Summer 2027
            </button>
            <button
              data-testid="hero-explore-button"
              onClick={() => scrollToSection("#manifesto")}
              className="rounded-full border border-[#F7F4EF]/35 px-8 py-4 text-sm font-semibold tracking-wide text-[#F7F4EF] backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-[#F7F4EF] hover:bg-[#F7F4EF]/10"
            >
              Explore the Cottage
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.45, duration: 1 }}
          className="mt-12 flex flex-wrap gap-3"
        >
          {BADGES.map((badge, i) => (
            <span
              key={badge}
              data-testid={`hero-badge-${i + 1}`}
              className="rounded-full border border-white/25 bg-white/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/85 backdrop-blur-md"
            >
              {badge}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToSection("#manifesto")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-[#F7F4EF]/70 transition-colors hover:text-[#F7F4EF]"
        aria-label="Scroll down"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={20} />
        </motion.div>
      </motion.button>
    </section>
  );
}
